import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ImageIcon, Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { uploadFile } from '@/lib/storage-adapter';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';

interface UniversalPhotoUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoUploaded: (photoUrl: string) => void;
  bucketName: string;
  folderPath: string;
  title?: string;
  description?: string;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];

const UniversalPhotoUploadDialog: React.FC<UniversalPhotoUploadDialogProps> = ({
  isOpen,
  onClose,
  onPhotoUploaded,
  bucketName,
  folderPath,
  title,
  description,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Reset states
    setError(null);
    setUploadStatus('idle');

    // Check file type
    if (!ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) {
      setError('Desteklenmeyen dosya formatı. Lütfen geçerli bir resim dosyası seçin (JPEG, PNG, GIF, WEBP).');
      return;
    }

    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('Dosya boyutu çok büyük. Lütfen 4MB\'dan küçük bir dosya seçin.');
      return;
    }

    // Preview image
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
    
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Lütfen bir resim seçin.');
      return;
    }

    setIsUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    setError(null);

    // Simulate initial progress (network connection and preparation phase)
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev < 95) {
          return prev + (Math.random() * 5);
        }
        return prev;
      });
    }, 300);

    try {
      const photoUrl = await uploadFile(file, bucketName, folderPath);
      
      // Set final progress and success state
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus('success');
      
      // Wait a moment to show the success state before closing
      setTimeout(() => {
        onPhotoUploaded(photoUrl);
        toast.success('Fotoğraf başarıyla yüklendi.');
        handleClose();
      }, 1000);
    } catch (error) {
      clearInterval(progressInterval);
      
      setUploadProgress(0);
      setUploadStatus('error');
      
      if (error instanceof Error) {
        setError(`Yükleme hatası: ${error.message}`);
      } else {
        setError('Fotoğraf yüklenirken bir bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
      }
      
      toast.error('Fotoğraf yüklenemedi.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) {
      // Ask for confirmation before closing during upload
      const shouldClose = window.confirm('Yükleme işlemi devam ediyor. Çıkmak istediğinize emin misiniz?');
      if (!shouldClose) return;
    }
    
    setFile(null);
    setPreviewUrl(null);
    setError(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    onClose();
  };

  const renderUploadState = () => {
    switch (uploadStatus) {
      case 'uploading':
        return (
          <div className="flex flex-col items-center mt-4 space-y-2">
            <Progress value={uploadProgress} className="w-full" />
            <div className="flex items-center text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              <span>Fotoğraf yükleniyor... (%{Math.round(uploadProgress)})</span>
            </div>
          </div>
        );
      case 'success':
        return (
          <div className="flex items-center mt-4 text-green-600">
            <CheckCircle2 className="h-5 w-5 mr-2" />
            <span>Yükleme başarılı!</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center mt-4 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error || 'Yükleme sırasında bir hata oluştu.'}</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {title || 'Fotoğraf Ekle'}
          </DialogTitle>
          <DialogDescription>
            {description || 'Bir fotoğraf yükleyebilirsiniz.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          
          <div className="flex items-center justify-center">
            <label 
              htmlFor="photo-upload" 
              className={`cursor-pointer border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center w-full hover:bg-muted/50 transition-colors ${error ? 'border-destructive' : ''}`}
            >
              {previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="max-h-48 object-contain mb-2" 
                />
              ) : (
                <ImageIcon className="h-12 w-12 text-muted-foreground mb-2" />
              )}
              <span className="text-sm text-muted-foreground">
                {previewUrl ? 'Fotoğrafı değiştirmek için tıklayın' : 'Fotoğraf yüklemek için tıklayın veya sürükleyin'}
              </span>
              <p className="text-xs text-muted-foreground mt-1">
                Maksimum 4MB - JPEG, PNG, GIF, WEBP
              </p>
              <input
                id="photo-upload"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || disabled}
              />
            </label>
          </div>
          
          {error && uploadStatus !== 'uploading' && uploadStatus !== 'error' && (
            <div className="flex items-center text-destructive text-sm">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          {renderUploadState()}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            {uploadStatus === 'success' ? 'Kapat' : 'İptal'}
          </Button>
          <Button 
            onClick={handleUpload} 
            disabled={!file || isUploading || uploadStatus === 'success' || disabled}
            className={uploadStatus === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Yükleniyor...
              </>
            ) : uploadStatus === 'success' ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Tamamlandı
              </>
            ) : (
              'Yükle'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UniversalPhotoUploadDialog;
